/**
 * MLS-PARCELS-001 — compact "MLS PCL 1 | MLS PCL 2 …" selector shown beside the
 * Municipal Lien Search Report heading, plus the delete-populated-parcel confirm.
 * PCL 1 (order.mls_search) is never deletable; PCL 2+ live in order.mls_parcels.
 */
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL;

export const mlsParcelList = (data) => [data?.mls_search || {}, ...((data?.mls_parcels) || [])];

export const mlsParcelPopulated = (p) => {
  if (!p) return false;
  if ((p.parcel_number || '').trim() || (p.date_prepared || '').trim() || (p.general_notes || '').trim()) return true;
  return ['code_violations', 'open_permits', 'utility_accounts', 'trash'].some((k) => {
    const sec = p[k] || {};
    return sec.none_found || (sec.entries || []).some((e) => Object.values(e || {}).some((v) => String(v || '').trim()));
  });
};

export const MlsParcelSelector = ({ orderId, parcels, active, onSelect, readOnly, onChanged }) => {
  const [pendingDelete, setPendingDelete] = useState(null);
  const [busy, setBusy] = useState(false);
  if (parcels.length <= 1) return null;

  const doDelete = async (idx) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/api/orders/${orderId}/components/mls/parcels/${idx}`);
      toast.success(`MLS PCL ${idx} deleted`);
      onSelect(Math.max(1, Math.min(active, parcels.length - 1)));
      onChanged?.();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Failed to delete MLS parcel');
    }
    setBusy(false); setPendingDelete(null);
  };
  const requestDelete = (idx) => (mlsParcelPopulated(parcels[idx - 1]) ? setPendingDelete(idx) : doDelete(idx));

  return (
    <>
      <div className="inline-flex items-center gap-1 ml-2 rounded-lg bg-slate-100/80 p-0.5" role="tablist" data-testid="mls-parcel-selector">
        {parcels.map((_, i) => {
          const idx = i + 1;
          const isActive = idx === active;
          return (
            <span key={idx} className="inline-flex items-center">
              <button type="button" role="tab" aria-selected={isActive} onClick={() => onSelect(idx)}
                className={`px-2.5 py-1 rounded-md text-[11px] font-bold tracking-wide transition-colors ${
                  isActive ? 'bg-white text-blue-700 shadow-sm border border-blue-200' : 'text-slate-500 hover:text-blue-600'}`}
                data-testid={`mls-parcel-tab-${idx}`}>
                MLS PCL {idx}
              </button>
              {isActive && idx > 1 && !readOnly && (
                <button type="button" onClick={() => requestDelete(idx)} disabled={busy} title={`Delete MLS PCL ${idx}`}
                  className="ml-0.5 h-6 w-6 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                  data-testid={`mls-parcel-delete-${idx}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              )}
            </span>
          );
        })}
      </div>
      <AlertDialog open={pendingDelete !== null} onOpenChange={(o) => { if (!o && !busy) setPendingDelete(null); }}>
        <AlertDialogContent className="sm:max-w-md" data-testid="mls-parcel-delete-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete MLS PCL {pendingDelete}?</AlertDialogTitle>
            <AlertDialogDescription>
              This parcel contains saved MLS information. Deleting it will permanently remove the parcel and its associated MLS data.
              Remaining parcels will be renumbered.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid="mls-parcel-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700" disabled={busy} onClick={() => doDelete(pendingDelete)}
              data-testid="mls-parcel-delete-confirm-btn">Delete Parcel</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default MlsParcelSelector;
